import { buildUrl, createRequest } from './common/httpUtils'

export const SAML_TENANTS_PATH = '/api/v1/saml_tenants'

/** アップロード可能なメタデータXMLの最大サイズ（4MB） */
export const METADATA_MAX_FILE_SIZE = 4 * 1024 * 1024

/** メタデータファイルとして許可するMIMEタイプ */
export const METADATA_ALLOWED_MIME_TYPES = ['application/xml', 'text/xml']

export type SamlTenant = {
  tenant_id: string
  entity_id: string
  sso_url: string | null
  slo_url: string | null
  enabled: boolean
  certificate_fingerprint?: string | null
}

export type SamlTenantUploadResult = {
  team_domain: string
  saml_tenant: SamlTenant
}

export type SamlTenantUploadParams = {
  teamDomain: string
  metadataFile?: File | null
  metadataXml?: string | null
  metadataUrl?: string | null
  enabled: boolean
}

export type SamlTenantError = Error & { status: number }

const STATUS_MESSAGES: { [status: number]: string } = {
  400: 'パラメータが不足しています。メタデータXMLまたはファイルを指定してください。',
  401: '認証が切れています。再度ログインしてください。',
  403: 'この操作はチーム管理者のみ実行できます。',
  404: '対象のチームが見つかりません。チームドメインを確認してください。',
  422: 'メタデータXMLまたは証明書の内容が不正です。Entraからダウンロードしたフェデレーションメタデータを確認してください。',
}

const responseErrorMessage = (data: any, status: number): string => {
  if (typeof data?.error_message === 'string' && data.error_message.length > 0) {
    return data.error_message
  }
  if (typeof data?.error === 'string' && data.error.length > 0) return data.error
  if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join('、')
  return STATUS_MESSAGES[status] ?? 'SAML設定の保存に失敗しました。'
}

const readJson = async (response: Response): Promise<any> => {
  try {
    return await response.json()
  } catch (error) {
    return {}
  }
}

/**
 * サーバー由来のメッセージに加えて、ステータスごとの案内文も添える。
 * 400/422はメタデータの内容が原因なので、再入力を促す文言を必ず表示する。
 */
const buildError = (data: any, status: number): SamlTenantError => {
  const serverMessage = responseErrorMessage(data, status)
  const guidance = STATUS_MESSAGES[status]
  const message = guidance && guidance !== serverMessage
    ? `${guidance}（${serverMessage}）`
    : serverMessage
  const error = new Error(message) as SamlTenantError
  error.status = status
  return error
}

/**
 * フェデレーションメタデータをアップロードしてTeamのSAML設定を登録・更新する。
 * ファイル指定時はmultipart/form-data、XML文字列指定時はJSONで送信する。
 */
export const createSamlTenant = async (
  params: SamlTenantUploadParams,
): Promise<SamlTenantUploadResult> => {
  const {
    teamDomain,
    metadataFile,
    metadataXml,
    metadataUrl,
    enabled,
  } = params
  const url = buildUrl(SAML_TENANTS_PATH)

  let request: Request
  if (metadataFile) {
    const formData = new FormData()
    formData.append('team_domain', teamDomain)
    formData.append('metadata_file', metadataFile)
    if (metadataUrl) formData.append('metadata_url', metadataUrl)
    formData.append('enabled', String(enabled))
    request = createRequest(url, 'POST', true, null, formData)
  } else {
    request = createRequest(url, 'POST', true, 'application/json', {
      team_domain: teamDomain,
      metadata_xml: metadataXml,
      ...(metadataUrl ? { metadata_url: metadataUrl } : {}),
      enabled,
    })
  }

  const response = await fetch(request)
  const data = await readJson(response)
  if (!response.ok) throw buildError(data, response.status)

  return data as SamlTenantUploadResult
}
